/**
 * Shared quality metadata extraction + normalization for KPI endpoint responses.
 *
 * Backing goal:
 * normalize quality contracts from multiple backend payload shapes without changing
 * each caller's request orchestration logic.
 */

const _isPlainObject = (value) =>
    Boolean(value) &&
    typeof value === 'object' &&
    !Array.isArray(value);

const _toUniqueList = (values) => {
    if (!Array.isArray(values)) {
        return [];
    }

    const unique = new Set();
    values.forEach((entry) => {
        if (typeof entry === 'string' && entry.trim().length > 0) {
            unique.add(entry.trim());
        }
        if (typeof entry === 'number' && !Number.isNaN(entry)) {
            unique.add(String(entry));
        }
    });

    return Array.from(unique);
};

const _coerceBoolean = (value) => {
    if (value === true || value === false) {
        return value;
    }
    if (typeof value === 'string') {
        const lowered = value.trim().toLowerCase();
        if (lowered === 'true') return true;
        if (lowered === 'false') return false;
        if (lowered === 'complete') return true;
        if (lowered === 'incomplete') return false;
        if (lowered === 'insufficient_data') return false;
        if (lowered === 'error') return false;
    }
    return null;
};

const _normalizeQuality = (qualityCandidate, sourceHint = '') => {
    if (typeof qualityCandidate === 'string') {
        return _normalizeQuality({ status: qualityCandidate }, sourceHint);
    }

    if (typeof qualityCandidate === 'boolean') {
        return _normalizeQuality({ calculation_complete: qualityCandidate }, sourceHint);
    }

    if (!_isPlainObject(qualityCandidate)) {
        return null;
    }

    const calculationComplete = _coerceBoolean(qualityCandidate.calculation_complete);
    const fallbackPolicy = qualityCandidate.fallback_policy;

    if (calculationComplete === null &&
        typeof qualityCandidate.status === 'string') {
        const status = qualityCandidate.status.toLowerCase();
        if (status === 'complete' || status === 'ok') {
            return {
                calculation_complete: true,
                required_data: _toUniqueList(qualityCandidate.required_data),
                warnings: _toUniqueList(qualityCandidate.warnings),
                fallback_policy: typeof fallbackPolicy === 'string' ? fallbackPolicy : null,
                source_hint: sourceHint || 'status',
            };
        }
        if (
            status === 'insufficient_data' ||
            status === 'error' ||
            status === 'incomplete'
        ) {
            return {
                calculation_complete: false,
                required_data: _toUniqueList(qualityCandidate.required_data),
                warnings: _toUniqueList(qualityCandidate.warnings),
                fallback_policy: typeof fallbackPolicy === 'string' ? fallbackPolicy : null,
                source_hint: sourceHint || 'status',
            };
        }
    }

    return {
        calculation_complete: calculationComplete,
        required_data: _toUniqueList(qualityCandidate.required_data),
        warnings: _toUniqueList(qualityCandidate.warnings),
        fallback_policy: typeof fallbackPolicy === 'string' ? fallbackPolicy : null,
        source_hint: sourceHint || null,
    };
};

const _extractMetricLevelQuality = (metricContainer, sourceHint = 'kpi_metadata') => {
    if (!_isPlainObject(metricContainer)) {
        return [];
    }

    const collected = [];
    Object.entries(metricContainer).forEach(([metricName, metricMetadata]) => {
        if (!_isPlainObject(metricMetadata)) {
            return;
        }
        const directCandidate = _normalizeQuality(
            metricMetadata.data_quality,
            `${sourceHint}:${metricName}:data_quality`
        );
        if (directCandidate) {
            collected.push(directCandidate);
            return;
        }
        if (_isPlainObject(metricMetadata.quality)) {
            const qualityCandidate = _normalizeQuality(
                metricMetadata.quality,
                `${sourceHint}:${metricName}:quality`
            );
            if (qualityCandidate) {
                collected.push(qualityCandidate);
            }
        }
    });

    return collected;
};

const _extractDirectQualityFromContainer = (container, sourceHint) => {
    if (!_isPlainObject(container)) {
        return [];
    }

    const direct = [];
    const directCandidate = _normalizeQuality(
        container.data_quality,
        `${sourceHint}:data_quality`
    );
    if (directCandidate) {
        direct.push(directCandidate);
    }

    if (_isPlainObject(container.metadata)) {
        const metadataCandidate = _normalizeQuality(
            container.metadata.data_quality || container.metadata.quality,
            `${sourceHint}:metadata`
        );
        if (metadataCandidate) {
            direct.push(metadataCandidate);
        }
    }

    if (_isPlainObject(container.kpi_metadata)) {
        const kpiMetadataCandidate = _normalizeQuality(
            container.kpi_metadata.data_quality || container.kpi_metadata.quality,
            `${sourceHint}:kpi_metadata`
        );
        if (kpiMetadataCandidate) {
            direct.push(kpiMetadataCandidate);
        } else {
            direct.push(..._extractMetricLevelQuality(
                container.kpi_metadata,
                `${sourceHint}:kpi_metadata`
            ));
        }
    }

    const qualityAliasCandidate = _normalizeQuality(
        container.quality,
        `${sourceHint}:quality`
    );
    if (qualityAliasCandidate) {
        direct.push(qualityAliasCandidate);
    }

    const statusCandidate = _normalizeQuality(
        container.status === 'ok' ||
            container.status === 'complete' ||
            container.status === 'insufficient_data' ||
            container.status === 'error'
            ? { status: container.status }
            : null,
        `${sourceHint}:status`
    );
    if (statusCandidate) {
        direct.push(statusCandidate);
    }

    return direct;
};

const _coalesceQualityCandidates = (responsePayload) => {
    const candidates = [];

    candidates.push(..._extractDirectQualityFromContainer(
        responsePayload,
        'root'
    ));
    candidates.push(..._extractDirectQualityFromContainer(
        responsePayload?.data,
        'data'
    ));

    if (_isPlainObject(responsePayload?.data?.metadata)) {
        candidates.push(..._extractDirectQualityFromContainer(
            responsePayload.data.metadata,
            'data.metadata'
        ));
    }

    if (_isPlainObject(responsePayload?.data?.kpi_metadata)) {
        candidates.push(..._extractDirectQualityFromContainer(
            responsePayload.data.kpi_metadata,
            'data.kpi_metadata'
        ));
        candidates.push(..._extractMetricLevelQuality(
            responsePayload.data.kpi_metadata,
            'data.kpi_metadata'
        ));
    }

    if (_isPlainObject(responsePayload?.metadata?.kpi_metadata)) {
        candidates.push(..._extractMetricLevelQuality(
            responsePayload.metadata.kpi_metadata,
            'metadata.kpi_metadata'
        ));
    }

    return candidates;
};

const _reduceQualityCandidates = (candidates) => {
    const result = candidates.filter(Boolean);
    if (result.length === 0) {
        return null;
    }

    let calculationState = null;
    const requiredData = new Set();
    const warnings = new Set();
    let fallbackPolicy = null;

    result.forEach((candidate) => {
        if (
            candidate.calculation_complete === true &&
            calculationState !== false
        ) {
            calculationState = true;
        }
        if (candidate.calculation_complete === false) {
            calculationState = false;
        }

        candidate.required_data.forEach((entry) => requiredData.add(entry));
        candidate.warnings.forEach((entry) => warnings.add(entry));
        if (!fallbackPolicy && candidate.fallback_policy) {
            fallbackPolicy = candidate.fallback_policy;
        }
    });

    return {
        calculation_complete: calculationState,
        required_data: Array.from(requiredData),
        warnings: Array.from(warnings),
        fallback_policy: fallbackPolicy,
    };
};

/**
 * True when a numeric KPI value is zero or zero-like (0, 0.0, "0").
 */
export const isZeroLikeKpiValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return false;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric === 0;
};

/**
 * True only when backend quality explicitly marks the metric complete.
 * Zero is displayable only when calculation_complete is true (not a silent fallback).
 */
export const isAuthoritativeKpiMetricValue = (value, qualitySummary) => {
    if (value === null || value === undefined) {
        return false;
    }
    if (!qualitySummary || qualitySummary.calculation_complete !== true) {
        return false;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric);
};

/**
 * Extract suspicious metric entries that look like numbers but lack complete quality.
 */
export const collectUnverifiedNumericMetrics = (responsePayload) => {
    const container = responsePayload?.data ?? responsePayload ?? {};
    const suspects = [];

    const inspectMetricMap = (metricMap, prefix) => {
        if (!_isPlainObject(metricMap)) {
            return;
        }
        Object.entries(metricMap).forEach(([metricName, metricMetadata]) => {
            if (!_isPlainObject(metricMetadata)) {
                if (metricMetadata !== null && metricMetadata !== undefined && Number.isFinite(Number(metricMetadata))) {
                    suspects.push({
                        metric: `${prefix}${metricName}`,
                        value: metricMetadata,
                        reason: 'numeric_without_metric_metadata',
                    });
                }
                return;
            }
            const value = metricMetadata.value ?? metricMetadata.metric_value;
            const quality = _normalizeQuality(
                metricMetadata.data_quality || metricMetadata.quality,
                `${prefix}${metricName}`
            );
            if (
                value !== null &&
                value !== undefined &&
                Number.isFinite(Number(value)) &&
                quality?.calculation_complete !== true
            ) {
                suspects.push({
                    metric: `${prefix}${metricName}`,
                    value,
                    reason: isZeroLikeKpiValue(value)
                        ? 'zero_without_complete_quality'
                        : 'numeric_without_complete_quality',
                });
            }
        });
    };

    inspectMetricMap(container.kpi_metadata, 'kpi_metadata.');
    inspectMetricMap(container.data?.kpi_metadata, 'data.kpi_metadata.');

    if (_isPlainObject(container.kpis)) {
        Object.entries(container.kpis).forEach(([metricName, metricValue]) => {
            if (
                metricValue !== null &&
                metricValue !== undefined &&
                Number.isFinite(Number(metricValue))
            ) {
                suspects.push({
                    metric: `kpis.${metricName}`,
                    value: metricValue,
                    reason: 'flat_kpi_map_without_metric_quality',
                });
            }
        });
    }

    return suspects;
};

const _isFailureResponse = (responsePayload) => {
    const payload = responsePayload || {};
    const data = payload.data || {};
    if (payload.status && payload.status >= 400) {
        return true;
    }
    return data.success === false || payload.success === false;
};

const _extractFailureWarnings = (responsePayload) => {
    const payload = responsePayload || {};
    const data = payload.data || {};
    return [
        payload.message,
        payload.error,
        data.message,
        data.error,
    ].filter(Boolean).map((entry) => String(entry));
};

/**
 * Extract and normalize a single endpoint's data quality summary.
 */
export const extractEndpointQuality = (response, endpoint, sourceTag, filters = null) => {
    const responsePayload = response?.data ?? {};
    const qualityFromPayload = _reduceQualityCandidates(
        _coalesceQualityCandidates(responsePayload)
    );

    const hadExplicitQuality = Boolean(qualityFromPayload);
    const isFailure = _isFailureResponse(response || {});

    if (!hadExplicitQuality) {
        const unverifiedMetrics = collectUnverifiedNumericMetrics(responsePayload);
        return {
            endpoint,
            source_tag: sourceTag || endpoint,
            filters,
            calculation_complete: false,
            required_data: [],
            warnings: isFailure
                ? _extractFailureWarnings(response || {})
                : [
                    'Missing data_quality contract',
                    ...unverifiedMetrics.map(
                        (entry) => `${entry.metric}: ${entry.reason}`
                    ),
                ],
            fallback_policy: isFailure ? 'strict' : 'strict',
        };
    }

    if (isFailure) {
        const allWarnings = Array.from(
            new Set([
                ...(qualityFromPayload.warnings || []),
                ..._extractFailureWarnings(response || {}),
            ])
        );
        return {
            endpoint,
            source_tag: sourceTag || endpoint,
            filters,
            calculation_complete: false,
            required_data: qualityFromPayload.required_data || [],
            warnings: allWarnings,
            fallback_policy: qualityFromPayload.fallback_policy || 'strict',
        };
    }

    const ambiguousQuality = qualityFromPayload.calculation_complete !== true &&
        qualityFromPayload.calculation_complete !== false;
    const warnings = Array.from(
        new Set([
            ...(qualityFromPayload.warnings || []),
            ...(ambiguousQuality ? ['Ambiguous calculation_complete state'] : []),
            ...collectUnverifiedNumericMetrics(responsePayload).map(
                (entry) => `${entry.metric}: ${entry.reason}`
            ),
        ])
    );

    return {
        endpoint,
        source_tag: sourceTag || endpoint,
        filters,
        calculation_complete: qualityFromPayload.calculation_complete === true,
        required_data: qualityFromPayload.required_data || [],
        warnings,
        fallback_policy: qualityFromPayload.fallback_policy || null,
    };
};

/**
 * Map a PromiseSettled-style API result into a normalized quality summary entry.
 */
export const collectSettledQuality = (result, endpoint, sourceTag, filters = null) => {
    if (result?.status === 'fulfilled') {
        return extractEndpointQuality(result.value, endpoint, sourceTag || endpoint, filters);
    }

    return {
        endpoint,
        source_tag: sourceTag || endpoint,
        filters,
        calculation_complete: false,
        required_data: [],
        warnings: ['Request failed'],
        fallback_policy: 'strict',
    };
};
